// Content-shaped shimmer placeholders replacing "Loading…" text.  Makes
// the app FEEL significantly faster even when the backend is the same
// speed — users see the layout land immediately + only the data fills in.
//
// Usage:
//   <Skeleton width="60%" height={20} />          — single bar
//   <SkeletonText lines={3} />                    — paragraph
//   <SkeletonTable rows={5} cols={4} />           — table shell
//   <SkeletonCard>                                — 96px card placeholder
//   <SkeletonMetric>                              — big-number KPI card
//
// The shimmer animation is a linear-gradient that translates left→right
// via the `animate-shimmer` class defined in Tailwind config OR the
// `<style>` block below (self-contained if the class isn't registered).

function baseClass() {
  // Uses Tailwind primary color tokens (bg-gray-100 for base + shimmer via
  // background-position animation).  Falls back gracefully if the theme
  // doesn't have primary-100.
  return 'bg-gray-100 rounded overflow-hidden relative before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent'
}

export function Skeleton({ width = '100%', height = 12, className = '', style = {} }) {
  return (
    <div
      className={`${baseClass()} ${className}`}
      style={{ width, height, ...style }}
      aria-hidden="true"
    />
  )
}

export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={12}
          width={i === lines - 1 ? '60%' : '100%'}
        />
      ))}
    </div>
  )
}

export function SkeletonTable({ rows = 5, cols = 4, className = '' }) {
  return (
    <div className={`w-full ${className}`} aria-hidden="true">
      {/* Header */}
      <div className="flex gap-3 pb-3 border-b border-gray-200 mb-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} height={10} width={`${100 / cols}%`} />
        ))}
      </div>
      {/* Rows */}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-3">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={c} height={14} width={`${100 / cols}%`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export function SkeletonCard({ height = 96, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-5 ${className}`} aria-hidden="true">
      <Skeleton height={12} width="40%" className="mb-3" />
      <Skeleton height={24} width="70%" className="mb-2" />
      <Skeleton height={10} width="30%" />
    </div>
  )
}

export function SkeletonMetric({ className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-5 ${className}`} aria-hidden="true">
      <div className="flex items-center gap-3 mb-3">
        <Skeleton height={32} width={32} className="rounded-full" />
        <Skeleton height={12} width="50%" />
      </div>
      <Skeleton height={28} width="70%" className="mb-1" />
      <Skeleton height={10} width="40%" />
    </div>
  )
}

// Invoice-shaped placeholder — letterhead block + bill-to grid + fees table
// + costs table + totals row.  Approximates the InvoiceDetail print layout so
// the page frame lands immediately while data loads.
export function SkeletonInvoice({ className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 ${className}`} aria-hidden="true">
      {/* Letterhead */}
      <div className="border-b border-gray-200 pb-4 mb-4" style={{ height: 80 }}>
        <Skeleton height={20} width="40%" className="mb-2" />
        <Skeleton height={10} width="30%" className="mb-1" />
        <Skeleton height={10} width="25%" />
      </div>
      {/* Bill-to + Invoice meta 2-col grid */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <Skeleton height={10} width="30%" className="mb-2" />
          <Skeleton height={14} width="70%" className="mb-1" />
          <Skeleton height={12} width="60%" className="mb-1" />
          <Skeleton height={12} width="50%" />
        </div>
        <div>
          <Skeleton height={10} width="40%" className="mb-2" />
          <Skeleton height={14} width="60%" className="mb-1" />
          <Skeleton height={12} width="50%" className="mb-1" />
          <Skeleton height={12} width="45%" />
        </div>
      </div>
      {/* Fees table */}
      <Skeleton height={12} width="20%" className="mb-2" />
      <SkeletonTable rows={4} cols={4} className="mb-6" />
      {/* Costs table */}
      <Skeleton height={12} width="20%" className="mb-2" />
      <SkeletonTable rows={3} cols={4} className="mb-6" />
      {/* Totals */}
      <div className="flex justify-end">
        <div style={{ width: '30%' }}>
          <Skeleton height={12} width="100%" className="mb-2" />
          <Skeleton height={12} width="100%" className="mb-2" />
          <Skeleton height={18} width="100%" />
        </div>
      </div>
    </div>
  )
}

// Month-view calendar grid placeholder — header row of 7 weekday labels +
// 6 rows × 7 cells of ~40px each.  Matches the standard month layout so the
// page frame lands without CLS while events load.
export function SkeletonCalendarGrid({ className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-4 ${className}`} aria-hidden="true">
      <div className="grid grid-cols-7 gap-2 mb-2">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} height={14} width="60%" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 42 }).map((_, i) => (
          <Skeleton key={i} height={40} width="100%" />
        ))}
      </div>
    </div>
  )
}

// Screen-reader friendly wrapper that pairs a skeleton with the announcement
// text for AT users while showing shimmer for sighted users.
export function LoadingRegion({ label = 'Loading content', children }) {
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
}

// Register the shimmer keyframes globally so consumers don't need Tailwind
// config changes to opt in.  Idempotent — the effect runs once on module
// load; re-runs are no-ops because we check for the existing style tag.
if (typeof document !== 'undefined' && !document.getElementById('lextrack-skeleton-shimmer')) {
  const style = document.createElement('style')
  style.id = 'lextrack-skeleton-shimmer'
  style.textContent = `
    @keyframes shimmer {
      100% { transform: translateX(100%); }
    }
  `
  document.head.appendChild(style)
}

export default Skeleton
